import { useState, useCallback } from 'react'
import { DataLayer } from '../lib/dataLayer'
import { Database } from '../types/database'

type Card = Database['public']['Tables']['cards']['Row']
type CardProgress = Database['public']['Tables']['user_card_progress']['Row']

export type CardAction = 'mastered' | 'recalled' | 'missed' | 'hide'

export interface ActionHistoryItem {
  cardId: string
  action: CardAction
  previousProgress: Partial<CardProgress> | null
  previousCardIndex: number
  previousCardsInRotation: Card[]
  previousAvailableCards: Card[]
}

export interface CardActionResult {
  newMasteryScore: number
  newStatus: 'active' | 'mastered' | 'hidden'
  shouldRemoveFromRotation: boolean
}

interface UseCardActionsOptions {
  userId: string
  onError?: (error: string) => void
}

/**
 * Hook for handling card actions (mastered, recalled, missed, hide) and undo.
 * Manages progress updates via DataLayer and maintains action history.
 */
export function useCardActions({ userId, onError }: UseCardActionsOptions) {
  const [actionHistory, setActionHistory] = useState<ActionHistoryItem[]>([])
  const [isProcessing, setIsProcessing] = useState(false)

  /**
   * Record action in history before making changes (for undo support)
   */
  const recordAction = useCallback((
    cardId: string,
    action: CardAction,
    currentProgress: Partial<CardProgress> | null,
    currentCardIndex: number,
    cardsInRotation: Card[],
    availableCards: Card[]
  ) => {
    setActionHistory(prev => [...prev, {
      cardId,
      action,
      previousProgress: currentProgress ? { ...currentProgress } : null,
      previousCardIndex: currentCardIndex,
      previousCardsInRotation: [...cardsInRotation],
      previousAvailableCards: [...availableCards]
    }])
  }, [])

  /**
   * Process a card action and update progress.
   * Returns the result so the component can handle rotation changes.
   */
  const handleAction = useCallback(async (
    cardId: string,
    action: CardAction
  ): Promise<CardActionResult | null> => {
    if (isProcessing) return null
    setIsProcessing(true)

    try {
      // Get current progress from local DB
      const allProgress = await DataLayer.getUserCardProgress(userId)
      const existingProgress = allProgress.find(p => p.card_id === cardId) || null

      let newMasteryScore = existingProgress?.mastery_score || 0
      let newStatus: 'active' | 'mastered' | 'hidden' = existingProgress?.status as any || 'active'

      // Update based on action
      switch (action) {
        case 'mastered':
          newStatus = 'mastered'
          newMasteryScore = 100
          break
        case 'recalled':
          newMasteryScore = Math.min(100, newMasteryScore + 20)
          // Auto-master if score reaches 100
          if (newMasteryScore === 100) {
            newStatus = 'mastered'
            console.log('[useCardActions] Card auto-mastered at 100% score')
          }
          break
        case 'missed':
          newMasteryScore = Math.max(0, newMasteryScore - 10)
          break
        case 'hide':
          newStatus = 'hidden'
          break
      }

      // Record the review in history
      const reviewResult = action === 'hide' ? 'hidden' : action
      await DataLayer.createReviewHistory(
        userId,
        cardId,
        reviewResult,
        existingProgress?.mastery_score || 0,
        newMasteryScore
      )

      // Upsert progress using DataLayer (local first, then sync)
      await DataLayer.upsertUserCardProgress({
        user_id: userId,
        card_id: cardId,
        mastery_score: newMasteryScore,
        status: newStatus,
        last_reviewed: new Date().toISOString(),
        review_count: (existingProgress?.review_count || 0) + 1,
        created_at: existingProgress?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString()
      })

      return {
        newMasteryScore,
        newStatus,
        shouldRemoveFromRotation: newStatus === 'mastered' || newStatus === 'hidden'
      }
    } catch (err: unknown) {
      onError?.(err instanceof Error ? err.message : 'Unknown error')
      return null
    } finally {
      setIsProcessing(false)
    }
  }, [userId, isProcessing, onError])

  /**
   * Undo the last action, restoring previous progress.
   * Returns the previous state so the component can restore rotation.
   */
  const undoLastAction = useCallback(async (): Promise<ActionHistoryItem | null> => {
    if (actionHistory.length === 0 || isProcessing) return null
    setIsProcessing(true)

    const lastAction = actionHistory[actionHistory.length - 1]

    try {
      // Restore previous progress in database using DataLayer
      if (lastAction.previousProgress) {
        console.log('[useCardActions] Undoing - restoring previous progress...')
        await DataLayer.upsertUserCardProgress({
          user_id: userId,
          card_id: lastAction.cardId,
          mastery_score: lastAction.previousProgress.mastery_score || 0,
          status: lastAction.previousProgress.status || 'active',
          last_reviewed: lastAction.previousProgress.last_reviewed || null,
          review_count: Math.max(0, (lastAction.previousProgress.review_count || 1) - 1),
          created_at: lastAction.previousProgress.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
      } else {
        console.log('[useCardActions] Undoing - no previous progress, resetting to initial state')
      }

      // Remove the last action from history
      setActionHistory(prev => prev.slice(0, -1))

      return lastAction
    } catch (err: unknown) {
      onError?.(`Failed to undo: ${err instanceof Error ? err.message : 'Unknown error'}`)
      return null
    } finally {
      setIsProcessing(false)
    }
  }, [actionHistory, userId, isProcessing, onError])

  /**
   * Clear action history (e.g., when starting a new session)
   */
  const clearHistory = useCallback(() => {
    setActionHistory([])
  }, [])

  /**
   * Check if undo is available
   */
  const canUndo = actionHistory.length > 0 && !isProcessing

  return {
    handleAction,
    undoLastAction,
    recordAction,
    clearHistory,
    actionHistory,
    canUndo,
    isProcessing
  }
}
